
import React from 'react';
import { useAdminGamesMutations } from '@/hooks/useAdminGamesMutations';
import { supabase } from '@/integrations/supabase/client';

interface AdminGamesHandlersProps {
  selectedGames: string[];
  bulkAction: string;
  bulkCategory: string;
  bulkFakePlayCount: string;
  bulkFakePlayMin: string;
  bulkFakePlayMax: string;
  bulkRatingsCountMin: string;
  bulkRatingsCountMax: string;
  useFakePlayRange: boolean;
  onClearBulkSelection: () => void;
  onStartProgress?: (action: string, totalGames: number) => void;
  onUpdateProgress?: (processedGames: number, results: any[]) => void;
  onCompleteProgress?: () => void;
}

export const useAdminGamesHandlers = ({
  selectedGames,
  bulkAction,
  bulkCategory,
  bulkFakePlayCount,
  bulkFakePlayMin,
  bulkFakePlayMax,
  bulkRatingsCountMin,
  bulkRatingsCountMax,
  useFakePlayRange,
  onClearBulkSelection,
  onStartProgress,
  onUpdateProgress,
  onCompleteProgress
}: AdminGamesHandlersProps) => {
  const {
    deleteGameMutation,
    bulkDeleteMutation,
    toggleVisibilityMutation,
    bulkUpdateMutation
  } = useAdminGamesMutations();

  // Handle select all on current page
  const handleSelectAll = (games: any[], setSelectedGames: (games: string[]) => void) => {
    return () => {
      if (selectedGames.length === games.length) {
        setSelectedGames([]);
      } else {
        setSelectedGames(games.map(game => game.id));
      }
    };
  };

  // Handle select all games across all pages with proper pagination
  const handleSelectAllPages = (setSelectedGames: (games: string[]) => void) => {
    return async () => {
      console.log('Selecting all games across all pages...');
      
      try {
        let allGameIds: string[] = [];
        let from = 0;
        const pageSize = 1000;
        let hasMore = true;

        while (hasMore) {
          const { data, error } = await supabase
            .from('games')
            .select('id')
            .range(from, from + pageSize - 1);
          
          if (error) {
            console.error('Error fetching games:', error);
            return;
          }
          
          if (data && data.length > 0) {
            const gameIds = data.map(game => game.id);
            allGameIds = [...allGameIds, ...gameIds];
            from += pageSize;
            hasMore = data.length === pageSize;
          } else {
            hasMore = false;
          }
        }
        
        setSelectedGames(allGameIds);
        console.log(`Selected ${allGameIds.length} games across all pages`);
      } catch (error) {
        console.error('Error selecting all games:', error);
      }
    };
  };

  // Handle individual selection
  const handleSelectGame = (setSelectedGames: (updater: (prev: string[]) => string[]) => void) => {
    return (gameId: string) => {
      setSelectedGames(prev =>
        prev.includes(gameId)
          ? prev.filter(id => id !== gameId)
          : [...prev, gameId]
      );
    };
  };

  // Enhanced bulk actions handler with progress tracking and no intermediate invalidations
  const handleBulkAction = async () => {
    if (selectedGames.length === 0) return;

    console.log(`Starting bulk action: ${bulkAction} for ${selectedGames.length} games`);
    
    // Start progress tracking
    onStartProgress?.(bulkAction, selectedGames.length);

    const progressCallback = (processedGames: number, results: any[]) => {
      console.log(`Progress: ${processedGames}/${selectedGames.length} games processed`);
      onUpdateProgress?.(processedGames, results);
    };

    try {
      switch (bulkAction) {
        case 'delete':
          await bulkDeleteMutation.mutateAsync({ 
            gameIds: selectedGames,
            onProgress: progressCallback
          });
          break;
        case 'hide':
          await bulkUpdateMutation.mutateAsync({ 
            gameIds: selectedGames, 
            updates: { hidden: true },
            onProgress: progressCallback
          });
          break;
        case 'show':
          await bulkUpdateMutation.mutateAsync({ 
            gameIds: selectedGames, 
            updates: { hidden: false },
            onProgress: progressCallback
          });
          break;
        case 'set-featured':
          await bulkUpdateMutation.mutateAsync({ 
            gameIds: selectedGames, 
            updates: { featured: true },
            onProgress: progressCallback
          });
          break;
        case 'unset-featured':
          await bulkUpdateMutation.mutateAsync({ 
            gameIds: selectedGames, 
            updates: { featured: false },
            onProgress: progressCallback
          });
          break;
        case 'set-trending':
          await bulkUpdateMutation.mutateAsync({ 
            gameIds: selectedGames, 
            updates: { trending: true },
            onProgress: progressCallback
          });
          break;
        case 'unset-trending':
          await bulkUpdateMutation.mutateAsync({ 
            gameIds: selectedGames, 
            updates: { trending: false },
            onProgress: progressCallback
          });
          break;
        case 'set-new':
          await bulkUpdateMutation.mutateAsync({ 
            gameIds: selectedGames, 
            updates: { is_new: true },
            onProgress: progressCallback
          });
          break;
        case 'unset-new':
          await bulkUpdateMutation.mutateAsync({ 
            gameIds: selectedGames, 
            updates: { is_new: false },
            onProgress: progressCallback
          });
          break;
        case 'change-category':
          if (bulkCategory) {
            await bulkUpdateMutation.mutateAsync({ 
              gameIds: selectedGames, 
              updates: { category: bulkCategory },
              onProgress: progressCallback
            });
          }
          break;
        case 'set-fake-plays':
          if (useFakePlayRange) {
            if (bulkFakePlayMin && bulkFakePlayMax && !isNaN(parseInt(bulkFakePlayMin)) && !isNaN(parseInt(bulkFakePlayMax))) {
              await bulkUpdateMutation.mutateAsync({ 
                gameIds: selectedGames, 
                updates: { fake_play_count: 0 },
                useFakePlayRange,
                bulkFakePlayMin,
                bulkFakePlayMax,
                onProgress: progressCallback
              });
            }
          } else {
            if (bulkFakePlayCount && !isNaN(parseInt(bulkFakePlayCount))) {
              await bulkUpdateMutation.mutateAsync({ 
                gameIds: selectedGames, 
                updates: { fake_play_count: parseInt(bulkFakePlayCount) },
                onProgress: progressCallback
              });
            }
          }
          break;
        case 'set-random-rating':
          if (bulkRatingsCountMin && bulkRatingsCountMax && !isNaN(parseInt(bulkRatingsCountMin)) && !isNaN(parseInt(bulkRatingsCountMax))) {
            await bulkUpdateMutation.mutateAsync({
              gameIds: selectedGames,
              updates: {
                rating: 'random_4_5',
                minRatingsCount: bulkRatingsCountMin,
                maxRatingsCount: bulkRatingsCountMax,
              },
              onProgress: progressCallback
            });
          }
          break;
      }
      
      console.log(`Bulk action ${bulkAction} completed successfully`);
    } catch (error) {
      console.error(`Bulk action ${bulkAction} failed:`, error);
    } finally {
      onCompleteProgress?.();
      onClearBulkSelection();
    }
  };

  // Handle toggle visibility
  const handleToggleVisibility = (gameId: string, hidden: boolean) => {
    toggleVisibilityMutation.mutate({ gameId, hidden });
  };

  // Handle delete game
  const handleDeleteGame = (gameId: string) => {
    deleteGameMutation.mutate(gameId);
  };

  return {
    handleSelectAll,
    handleSelectAllPages,
    handleSelectGame,
    handleBulkAction,
    handleToggleVisibility,
    handleDeleteGame,
    isLoading: bulkUpdateMutation.isPending || bulkDeleteMutation.isPending
  };
};
